/* eslint-disable max-lines -- Lint comments put it over, and because it's ECC it's fine to be over the max lines */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs';

import {
  EMPLOYEE_SEARCH_SELECT_OPTIONS,
  SAP_UNAVAILABLE_ERROR_CODES,
  SAP_UNAVAILABLE_MESSAGE_PARTIALS,
} from '@shared-constants/odata-constants';
import { LocalStorageKeyEnum } from '@shared-enums/local-storage.enum';
import { arrayIsTruthy } from '@shared-helpers/array-object-helpers';
import { checkLocalStorage } from '@shared-helpers/storage-helpers';
import { trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import * as sharedApi from '@shared-interfaces/api';
import { IOutgoingCatRecords } from '@shared-interfaces/cats-db';
import { IExtDocument } from '@shared-interfaces/documents';
import { IHttpErrorResponseObject } from '@shared-interfaces/error';

import { environment } from '../../environments/environment';
import * as apiModel from '../models/api.model';
import { IGetForemanTimesheetBodyObject } from '../models/api.model';
import { IContract } from '../pages/contract/contract.model';
import { LogRocketService } from './log-rocket.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly baseUrl: string = environment.webServerUrl;
  private readonly url: string = `${this.baseUrl}api/`;
  private readonly timberTrapUrl = environment.timberTrapUrl;

  private approversCache$?: Observable<sharedApi.IWebServerResponseApproverList[]>;

  constructor(
    private readonly http: HttpClient,
    private readonly logRocketService: LogRocketService,
  ) {}

  public sendEmail$(message: string, recipientEmail: string, subject: string): Observable<sharedApi.ISimpleEmailResponse> {
    return this.http.post<sharedApi.ISimpleEmailResponse>(`${this.timberTrapUrl}simpleEmail`, {
      message,
      recipientEmail,
      subject,
    });
  }

  public getProductionGroupId$(): Observable<string> {
    const fullUrl = `${this.url}prodGroup`;
    return this.http
      .get<sharedApi.IHttpResponseGetProductionGroupId>(fullUrl)
      .pipe(map((value) => value.GetProdGroupID.Prdgrpid));
  }

  public getEmployees$(queryParams: apiModel.ISapRequestFilterQueryProps): Observable<sharedApi.IHttpResponseEmployee[]> {
    const fullUrl = `${this.url}getEmployees`;
    const params = new HttpParams().set('$expand', 'EmpToActivity').set('$filter', this.makeFilterByObj(queryParams));
    return this.http
      .get<sharedApi.IHttpResponseEmployee[] | undefined>(fullUrl, { params: params })
      .pipe(map((value) => value ?? []));
  }

  public getEmployeesForSearch$(
    queryParams: apiModel.ISapRequestFilterQueryProps,
    shouldSearchAllCostCenters = false,
  ): Observable<sharedApi.IHttpResponseSearchableEmployee[]> {
    const fullUrl = `${this.url}getEmployees`;
    const params = new HttpParams()
      // "and CostCenter" makes it so anyone without a cost center won't show
      .set('$filter', `${this.makeFilterByObj(queryParams)} ${shouldSearchAllCostCenters ? "and CostCenter ne ''" : ''}`)
      .set('$select', EMPLOYEE_SEARCH_SELECT_OPTIONS);
    return this.http.get<sharedApi.IHttpResponseSearchableEmployee[] | undefined>(fullUrl, { params: params }).pipe(
      map((value) => {
        if (!value) return [];
        return value.map((employee) => ({
          ...employee,
          EmpId: trimLeadingZeros(employee.EmpId),
        }));
      }),
    );
  }

  public getOneEmployee$(
    documentNum: string,
    date: string,
    userEmployeeNum: string,
    employeeNum: string,
  ): Observable<sharedApi.IForemanEmployee> {
    return this.http.get<sharedApi.IForemanEmployee>(
      `${this.url}foreman/employee/${documentNum}/${date}/${userEmployeeNum}/${employeeNum}`,
    );
  }

  public getSelf$(): Observable<sharedApi.IHttpResponseUser> {
    const fullUrl = `${this.url}whoami`;
    return this.http.get<sharedApi.IHttpResponseUser>(fullUrl, { withCredentials: true });
  }

  public getApprovers$(): Observable<sharedApi.IWebServerResponseApproverList[]> {
    if (this.approversCache$ == null) {
      const fullUrl = `${this.url}approver`;
      const params = new HttpParams().set('viewOnly', false);
      this.approversCache$ = this.http
        .get<sharedApi.IWebServerResponseApproverList[]>(fullUrl, { params: params })
        .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    }
    return this.approversCache$;
  }

  public getContractsByDateAndCC$(costCenter: string, date: string): Observable<IContract[]> {
    const fullUrl = `${this.url}getContracts`;
    const params = new HttpParams().set(
      '$filter',
      `CostCenter eq '${costCenter}' and ( ValidFrom le ${date} and ValidTo ge ${date})`,
    );
    return this.http.get<IContract[] | undefined>(fullUrl, { params: params }).pipe(map((value) => value ?? []));
  }

  public getContracts$(queryParams: apiModel.ISapRequestFilterQueryProps): Observable<sharedApi.IHttpResponseContract[]> {
    const fullUrl = `${this.url}getContracts`;
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams));
    return this.http
      .get<sharedApi.IHttpResponseContract[] | undefined>(fullUrl, { params: params })
      .pipe(map((res) => res ?? []));
  }

  public getCostCenters$(queryParams: apiModel.ISapRequestFilterQueryProps): Observable<sharedApi.IHttpResponseCostCenter[]> {
    const fullUrl = `${this.url}getCostCenters`;
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams));
    return this.http
      .get<sharedApi.IHttpResponseCostCenter[] | undefined>(fullUrl, { params: params })
      .pipe(map((value) => value ?? []));
  }

  public getForemanTimesheet$(params: IGetForemanTimesheetBodyObject): Observable<sharedApi.IWebServerResponseForemanTimesheet> {
    return this.http.post<sharedApi.IWebServerResponseForemanTimesheet>(`${this.url}foreman/timesheet`, params);
  }

  public getContractConditions$(
    queryParams: apiModel.ISapRequestFilterQueryProps,
  ): Observable<sharedApi.ISapResponseContractCondition[]> {
    const fullUrl = `${this.url}getContractConditions`;
    const params = new HttpParams()
      .set('$filter', this.makeFilterByObj(queryParams))
      .set('$expand', 'CondToSkf,CondToSkf/SkfToValue,CondToMat,CondToDefault')
      .set('$format', 'json');
    return this.http
      .get<sharedApi.ISapResponseContractCondition[] | undefined>(fullUrl, { params: params })
      .pipe(map((value) => value ?? []));
  }

  public postRecords$<T>(recordsObj: IOutgoingCatRecords<T>): Observable<unknown[]> {
    if (!arrayIsTruthy(recordsObj.d.CatsHeadtoItem)) return of([]);
    const fullUrl = `${this.url}catsdb`;
    return this.http
      .post<sharedApi.ICatsHeadToItemHttpPostResultsObj<unknown[]>>(fullUrl, recordsObj)
      .pipe(map((value) => value.CatsHeadtoItem.results));
  }

  public getEquipment$(queryParams: apiModel.ISapRequestFilterQueryProps): Observable<sharedApi.IHttpResponseEquipment[]> {
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams));
    return this.http
      .get<sharedApi.IHttpResponseEquipment[] | undefined>(`${this.url}getEquipment`, { params: params })
      .pipe(map((value) => value ?? []));
  }

  public getEquipmentByPartialDescription$(
    queryParams: apiModel.ISapRequestFilterQueryProps,
  ): Observable<sharedApi.IHttpResponseEquipment[]> {
    const params = new HttpParams().set('$filter', `${this.makeFilterByObj(queryParams)} and CostCenter ne ''`);
    return this.http
      .get<sharedApi.IHttpResponseEquipment[] | undefined>(`${this.url}getEquipment`, { params: params })
      .pipe(map((value) => value ?? []));
  }

  public getOneEquipment$(
    documentNum: string,
    date: string,
    userEmployeeNum: string,
    equipmentNum: string,
  ): Observable<sharedApi.IForemanEquipment> {
    return this.http.get<sharedApi.IForemanEquipment>(
      `${this.url}foreman/equipment/${documentNum}/${date}/${userEmployeeNum}/${equipmentNum}`,
    );
  }

  /**@deprecated this is only needed until oleg fixes his code. */
  public oldGetOneEquipment$(
    queryParams: apiModel.ISapRequestFilterQueryProps,
  ): Observable<sharedApi.IHttpResponseEquipment | undefined> {
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams));
    return this.http
      .get<sharedApi.IHttpResponseEquipment[]>(`${this.url}getEquipment`, { params: params })
      .pipe(map((value) => value[0]));
  }

  public getTimesheets$(queryParams: apiModel.ISapRequestFilterQueryProps): Observable<IExtDocument[]> {
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams));
    const fullUrl = `${this.url}getTimesheet`;
    return this.http.get<IExtDocument[] | undefined>(fullUrl, { params: params }).pipe(map((value) => value ?? []));
  }

  public getOneTimesheet$(queryParams: apiModel.ISapRequestFilterQueryProps): Observable<IExtDocument | undefined> {
    const params = new HttpParams().set('$filter', this.makeFilterByObj(queryParams));
    const fullUrl = `${this.url}getTimesheet`;
    return this.http.get<IExtDocument[]>(fullUrl, { params: params }).pipe(map((value) => value[0]));
  }

  public updateExtDocument$(body: Partial<IExtDocument>): Observable<unknown> {
    const fullUrl = `${this.url}timesheet`;
    return this.http.patch<unknown>(fullUrl, body);
  }

  public logError$(
    message: string,
    serverError?: IHttpErrorResponseObject,
    url?: string,
  ): Observable<sharedApi.ITimberTrapLogResponse> {
    let severity: sharedApi.ITimberTrapSeverity = 4;
    if (serverError != null) {
      severity = 3; //more important if we have an error object
      if (
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- In ECC we know it's not null and don't want to change it too much.
        SAP_UNAVAILABLE_ERROR_CODES.includes(serverError.code!) ||
        SAP_UNAVAILABLE_MESSAGE_PARTIALS.some((p) => serverError.technicalMessage.toLowerCase().includes(p))
      ) {
        severity = 1; //It looks like SAP is down, so bump up the severity to the max
      }
    }
    if (!environment.production) severity = 5; // if not production, it's even less important, so we just log it

    const fullObj: sharedApi.ITimberTrapRequestBody = {
      application: environment.application,
      version: environment.appVersion,
      environment: environment.environmentName,
      severity,
      userName: this.logRocketService.userName,
      user: checkLocalStorage<string>(LocalStorageKeyEnum.UserId) ?? '',
      eventName: url,
      custom: this.logRocketService.url,
      message,
    };

    //If this is a server-side error, add as many details as we have
    if (serverError != null) {
      fullObj.custom2 = serverError.technicalMessage;
      fullObj.errorCode = serverError.code;
      if (serverError.odataDetails != null) fullObj.custom3 = JSON.stringify(serverError.odataDetails, null, 2);
      console.error('Back-End Service Error!', serverError);
    }
    return this.http.post<sharedApi.ITimberTrapLogResponse>(`${this.timberTrapUrl}log`, fullObj, {
      headers: new HttpHeaders().set('Authorization', environment.timberTrapKey),
    });
  }

  public signOut$(): Observable<unknown> {
    const fullUrl = `${this.url}logoff`;
    return this.http.get<unknown>(fullUrl);
  }

  private makeFilterByObj(obj: apiModel.ISapRequestFilterQueryProps): string {
    let str = '';
    Object.keys(obj).forEach((key, index) => {
      const value = obj[key];
      if (Array.isArray(value)) {
        str += this.handleFilterArrayCase(key, value, index);
      } else if (typeof value === 'string') {
        str += this.handleFilterStringCase(key, value, index, 'and');
      }
    });
    return str;
  }

  private handleFilterArrayCase(key: string, arrayParam: string[], loopCount: number): string {
    let str = '';
    if (!arrayIsTruthy(arrayParam)) return str;
    arrayParam.forEach((value, index) => {
      str += this.handleFilterStringCase(key, value, index, 'or');
    });
    if (loopCount > 0) {
      return ` and (${str})`;
    } else {
      return `(${str})`;
    }
  }

  private handleFilterStringCase(key: string, value: string, index: number, queryJoin: 'and' | 'or'): string {
    let valueForQuery = value;
    if (!/datetime/gim.test(value)) {
      valueForQuery = `'${value}'`;
    }
    if (key.trim() === '') {
      return '';
    } else if (index === 0) {
      return `${key} eq ${valueForQuery}`;
    } else {
      return ` ${queryJoin} ${key} eq ${valueForQuery}`;
    }
  }
}
